<template>
  <div
    v-if="isActive && layout"
    class="anchored-callout"
    :style="{ zIndex: zIndex }"
  >
    <svg class="anchored-callout__line-layer">
      <line
        class="anchored-callout__line"
        :x1="layout.lineStart.x"
        :y1="layout.lineStart.y"
        :x2="layout.lineEnd.x"
        :y2="layout.lineEnd.y"
        :stroke="lineColor"
        :stroke-width="lineWidth"
        :style="lineStyle"
        stroke-linecap="round"
      />
    </svg>

    <div
      class="anchored-callout__ring"
      :style="ringStyle"
    ></div>

    <div
      ref="balloon"
      class="anchored-callout__balloon"
      :style="balloonStyle"
    >
      <button
        type="button"
        class="anchored-callout__close"
        aria-label="Dismiss callout"
        @click="dismiss"
      >
        x
      </button>
      <slot>
        {{ text }}
      </slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AnchoredCallout',
  props: {
    value: {
      type: Boolean,
      default: true,
    },
    text: {
      type: String,
      default: '',
    },
    targetSelector: {
      type: String,
      required: true,
    },
    placement: {
      type: String,
      default: 'left',
      validator: value => ['left', 'right', 'top', 'bottom', 'auto'].includes(value),
    },
    storageKey: {
      type: String,
      default: '',
    },
    showOnce: {
      type: Boolean,
      default: false,
    },
    balloonWidth: {
      type: Number,
      default: 220,
    },
    targetPadding: {
      type: Number,
      default: 10,
    },
    gap: {
      type: Number,
      default: 22,
    },
    viewportPadding: {
      type: Number,
      default: 16,
    },
    rotation: {
      type: Number,
      default: -5,
    },
    zIndex: {
      type: Number,
      default: 20,
    },
    lineColor: {
      type: String,
      default: '#f44336',
    },
    lineWidth: {
      type: Number,
      default: 3,
    },
    ringColor: {
      type: String,
      default: '#f44336',
    },
    ringWidth: {
      type: Number,
      default: 4,
    },
    backgroundColor: {
      type: String,
      default: '#ffffff',
    },
    borderColor: {
      type: String,
      default: '#f44336',
    },
    textColor: {
      type: String,
      default: '#111111',
    },
  },
  data: () => ({
    frameId: null,
    layout: null,
    targetMissing: true,
    dismissedFromStorage: false,
    hasMarkedSeen: false,
    balloonSize: {
      width: 220,
      height: 120,
    },
  }),
  computed: {
    isActive() {
      return this.value && !this.dismissedFromStorage && !this.targetMissing
    },
    ringStyle() {
      if (!this.layout) return {}

      return {
        left: `${this.layout.ring.left}px`,
        top: `${this.layout.ring.top}px`,
        width: `${this.layout.ring.size}px`,
        height: `${this.layout.ring.size}px`,
        borderColor: this.ringColor,
        borderWidth: `${this.ringWidth}px`,
      }
    },
    balloonStyle() {
      if (!this.layout) return {}

      return {
        left: `${this.layout.balloon.left}px`,
        top: `${this.layout.balloon.top}px`,
        width: `${this.balloonWidth}px`,
        '--callout-rotation': `${this.rotation}deg`,
        backgroundColor: this.backgroundColor,
        borderColor: this.borderColor,
        color: this.textColor,
      }
    },
    lineStyle() {
      if (!this.layout) return {}

      return {
        '--line-length': `${this.layout.lineLength}px`,
      }
    },
  },
  watch: {
    value(active) {
      if (active) {
        this.targetMissing = false
        this.startTracking()

        return
      }

      this.stopTracking()
      this.layout = null
    },
    targetSelector() {
      this.layout = null
      this.startTracking()
    },
  },
  mounted() {
    this.dismissedFromStorage = this.showOnce && this.storageKey
      ? localStorage.getItem(this.storageKey) === '1'
      : false
    this.startTracking()
  },
  beforeDestroy() {
    this.stopTracking()
  },
  methods: {
    startTracking() {
      this.stopTracking()

      if (!this.value || this.dismissedFromStorage) {
        return
      }

      const run = () => {
        this.updateLayout()
        if (this.value && !this.dismissedFromStorage) {
          this.frameId = window.requestAnimationFrame(run)
        }
      }

      this.frameId = window.requestAnimationFrame(run)
    },
    stopTracking() {
      if (this.frameId) {
        window.cancelAnimationFrame(this.frameId)
        this.frameId = null
      }
    },
    getTargetElement() {
      return document.querySelector(this.targetSelector)
    },
    updateLayout() {
      const target = this.getTargetElement()

      if (!target) {
        this.targetMissing = true
        this.layout = null

        return
      }

      const rect = target.getBoundingClientRect()

      if (!rect.width && !rect.height) {
        this.targetMissing = true
        this.layout = null

        return
      }

      this.targetMissing = false
      this.measureBalloon()

      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const ringSize = Math.max(rect.width, rect.height) + (this.targetPadding * 2)
      const ringCenter = {
        x: rect.left + (rect.width / 2),
        y: rect.top + (rect.height / 2),
      }
      const placement = this.resolvePlacement(ringCenter, viewportWidth, viewportHeight)
      const balloon = this.calculateBalloonPosition(placement, ringCenter, ringSize, viewportWidth, viewportHeight)
      const lineStart = this.getBalloonAnchor(placement, balloon)

      this.layout = {
        placement,
        balloon,
        ring: {
          left: ringCenter.x - (ringSize / 2),
          top: ringCenter.y - (ringSize / 2),
          size: ringSize,
        },
        lineStart,
        lineEnd: this.getRingIntersection(lineStart, ringCenter, (ringSize / 2) + (this.ringWidth / 2)),
        lineLength: 0,
      }

      this.layout.lineLength = this.getLineLength(this.layout.lineStart, this.layout.lineEnd)

      if (!this.hasMarkedSeen && this.showOnce && this.storageKey) {
        this.hasMarkedSeen = true
        localStorage.setItem(this.storageKey, '1')
        this.$emit('shown')
      }
    },
    measureBalloon() {
      const balloon = this.$refs.balloon

      if (!balloon) {
        return
      }

      const nextHeight = balloon.offsetHeight || this.balloonSize.height
      const nextWidth = balloon.offsetWidth || this.balloonWidth

      if (nextHeight !== this.balloonSize.height || nextWidth !== this.balloonSize.width) {
        this.balloonSize = {
          width: nextWidth,
          height: nextHeight,
        }
      }
    },
    resolvePlacement(ringCenter, viewportWidth, viewportHeight) {
      if (this.placement !== 'auto') {
        return this.placement
      }

      const horizontalSpace = {
        left: ringCenter.x,
        right: viewportWidth - ringCenter.x,
      }
      const verticalSpace = {
        top: ringCenter.y,
        bottom: viewportHeight - ringCenter.y,
      }

      if (Math.max(horizontalSpace.left, horizontalSpace.right) >= Math.max(verticalSpace.top, verticalSpace.bottom)) {
        return horizontalSpace.left > horizontalSpace.right ? 'left' : 'right'
      }

      return verticalSpace.top > verticalSpace.bottom ? 'top' : 'bottom'
    },
    calculateBalloonPosition(placement, ringCenter, ringSize, viewportWidth, viewportHeight) {
      const width = this.balloonWidth
      const height = this.balloonSize.height
      const padding = this.viewportPadding
      let left = ringCenter.x - (width / 2)
      let top = ringCenter.y - (height / 2)

      if (placement === 'left') {
        left = ringCenter.x - (ringSize / 2) - this.gap - width
        top = ringCenter.y - (height / 2)
      }

      if (placement === 'right') {
        left = ringCenter.x + (ringSize / 2) + this.gap
        top = ringCenter.y - (height / 2)
      }

      if (placement === 'top') {
        left = ringCenter.x - (width / 2)
        top = ringCenter.y - (ringSize / 2) - this.gap - height
      }

      if (placement === 'bottom') {
        left = ringCenter.x - (width / 2)
        top = ringCenter.y + (ringSize / 2) + this.gap
      }

      return {
        left: this.clamp(left, padding, viewportWidth - width - padding),
        top: this.clamp(top, padding, viewportHeight - height - padding),
        width,
        height,
      }
    },
    getBalloonAnchor(placement, balloon) {
      const middleX = balloon.left + (balloon.width / 2)
      const middleY = balloon.top + (balloon.height / 2)

      if (placement === 'left') {
        return {
          x: balloon.left + balloon.width,
          y: middleY,
        }
      }

      if (placement === 'right') {
        return {
          x: balloon.left,
          y: middleY,
        }
      }

      if (placement === 'top') {
        return {
          x: middleX,
          y: balloon.top + balloon.height,
        }
      }

      return {
        x: middleX,
        y: balloon.top,
      }
    },
    getRingIntersection(start, center, radius) {
      const deltaX = start.x - center.x
      const deltaY = start.y - center.y
      const distance = Math.hypot(deltaX, deltaY) || 1

      return {
        x: center.x + ((deltaX / distance) * radius),
        y: center.y + ((deltaY / distance) * radius),
      }
    },
    getLineLength(start, end) {
      return Math.hypot(end.x - start.x, end.y - start.y)
    },
    dismiss() {
      this.stopTracking()
      this.layout = null
      this.$emit('close')
      this.$emit('input', false)
    },
    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max)
    },
  },
}
</script>

<style scoped lang="scss">
.anchored-callout {
  position: fixed;
  inset: 0;
  pointer-events: none;
}

.anchored-callout__line-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.anchored-callout__line {
  stroke-dasharray: var(--line-length);
  stroke-dashoffset: var(--line-length);
  animation: anchored-callout-line-in 380ms ease-out 120ms forwards;
}

.anchored-callout__ring {
  position: fixed;
  border-style: solid;
  border-radius: 999px;
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.95);
  animation: anchored-callout-ring-in 280ms ease-out forwards;
}

.anchored-callout__balloon {
  position: fixed;
  padding: 14px 16px;
  border: 4px solid;
  border-radius: 18px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
  font-size: 20px;
  font-weight: 900;
  font-style: italic;
  line-height: 1.05;
  text-align: center;
  text-transform: uppercase;
  font-family: 'Arial Black', 'Segoe UI', sans-serif;
  pointer-events: auto;
  --callout-rotation: 0deg;
  animation: anchored-callout-balloon-in 320ms cubic-bezier(0.22, 1, 0.36, 1) forwards;

  @media(min-width: 941px) {
    padding-right: 34px;
  }

  @media(max-width: 940px) {
    width: 180px !important;
    padding: 12px 14px;
    padding-right: 30px;
    font-size: 15px;
  }
}

.anchored-callout__close {
  position: absolute;
  top: 6px;
  right: 8px;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  font-size: 16px;
  font-weight: 900;
  line-height: 1;
  cursor: pointer;
}

@keyframes anchored-callout-balloon-in {
  from {
    opacity: 0;
    transform: rotate(var(--callout-rotation)) translateY(8px) scale(0.92);
  }

  to {
    opacity: 1;
    transform: rotate(var(--callout-rotation)) translateY(0) scale(1);
  }
}

@keyframes anchored-callout-ring-in {
  from {
    opacity: 0;
    transform: scale(0.7);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes anchored-callout-line-in {
  from {
    opacity: 0;
    stroke-dashoffset: var(--line-length);
  }

  to {
    opacity: 1;
    stroke-dashoffset: 0;
  }
}
</style>